import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Plus, PencilLine, Trash2, EyeOff, Send } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import BlogEditorDialog from "./BlogEditorDialog";
import { fmtDate } from "@/components/blog/PostCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconBtn = "inline-flex h-8 w-8 items-center justify-center rounded-sm border border-slate-200 hover:border-slate-900 text-slate-700 transition-colors";

function StatusBadge({ status }) {
  const pub = status === "published";
  return <span className={`mono text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 border ${pub ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{pub ? "Published" : "Draft"}</span>;
}

/** Admin blog list: create, edit, publish/unpublish, delete. */
export default function BlogManager({ token }) {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState({ open: false, post: null });
  const [toDelete, setToDelete] = useState(null);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/blog/posts`, { headers }).then((r) => setPosts(r.data)).catch(() => toast.error("Could not load posts."));
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => [...new Set(posts.map((p) => p.category))], [posts]);

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    const { id, read_minutes, published_at, created_at, updated_at, seeded, ...body } = p;
    try {
      await axios.put(`${API}/admin/blog/posts/${id}`, { ...body, status: next }, { headers });
      toast.success(next === "published" ? "Post published." : "Post moved to drafts.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/blog/posts/${toDelete.id}`, { headers });
      toast.success("Post deleted.");
      setToDelete(null);
      load();
    } catch { toast.error("Delete failed."); }
  };

  return (
    <section data-testid="blog-manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Blog engine</div>
          <h2 className="display mt-2 text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900">{posts.length} posts · {posts.filter((p) => p.status === "published").length} live</h2>
        </div>
        <button onClick={() => setEditing({ open: true, post: null })} data-testid="blog-new-post" className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2">
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-left" data-testid="blog-admin-table">
          <thead className="bg-paper-dark/40">
            <tr>{["Title", "Category", "Target keyword", "Status", "Updated", ""].map((c) => <th key={c} className="p-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500 whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-slate-100" data-testid={`blog-admin-row-${p.slug}`}>
                <td className="p-3 text-sm font-semibold text-slate-900 max-w-md">{p.title}<div className="mono text-[10px] text-slate-400 font-normal">/blog/{p.slug}</div></td>
                <td className="p-3 text-xs text-slate-600 whitespace-nowrap">{p.category}</td>
                <td className="p-3 text-xs text-slate-600">{p.target_keyword || "—"}</td>
                <td className="p-3"><StatusBadge status={p.status} /></td>
                <td className="p-3 mono text-[11px] text-slate-500 whitespace-nowrap">{fmtDate(p.updated_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1.5 justify-end">
                    {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className={iconBtn} title="View" data-testid={`blog-view-${p.slug}`}><ExternalLink className="h-3.5 w-3.5" /></a>}
                    <button className={iconBtn} title="Edit" onClick={() => setEditing({ open: true, post: p })} data-testid={`blog-edit-${p.slug}`}><PencilLine className="h-3.5 w-3.5" /></button>
                    <button className={iconBtn} title={p.status === "published" ? "Unpublish" : "Publish"} onClick={() => toggleStatus(p)} data-testid={`blog-toggle-${p.slug}`}>{p.status === "published" ? <EyeOff className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}</button>
                    <button className={`${iconBtn} hover:border-rose-600 hover:text-rose-600`} title="Delete" onClick={() => setToDelete(p)} data-testid={`blog-delete-${p.slug}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BlogEditorDialog open={editing.open} post={editing.post} categories={categories} token={token}
        onClose={() => setEditing({ open: false, post: null })}
        onSaved={() => { setEditing({ open: false, post: null }); load(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="blog-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be removed from the site and sitemap. This can't be undone. Unpublish instead if you may need it later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="blog-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="blog-delete-confirm">Delete post</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
